import { useContext, useEffect, useState } from 'react'

import Grid from '@mui/material/Grid2'
import Typography from '@mui/material/Typography'

import Divider from '@mui/material/Divider'
import { SetupDetails } from '../../../api'
import { AppStateContext } from '../../../state/AppProvider'
import { ToastMessage } from '../../ExperimentForm/ExperimentFormToast'
import { DateFormField } from '../../form/DateFormField'
import { FileInputFormField } from '../../form/FileInputFormField'
import { MultiSelectFormField } from '../../form/MultiSelectFormField'
import { NumberFormField } from '../../form/NumberFormField'
import { TextFormField } from '../../form/TextFormField'
import styles from './SetupDetailsStep.module.css'

interface Props {
  onError?: (message: Pick<ToastMessage, 'title' | 'description' | 'type'>) => void
}

export const SetupDetailsStep = ({ onError }: Props) => {
  const appStateContext = useContext(AppStateContext)
  const [data, setData] = useState<SetupDetails | null>(null)
  const [convId, setConvId] = useState<string>('')

  // Retrieve the current chat data
  useEffect(() => {
    if (appStateContext?.state.currentChat?.extended_data?.experiment?.setup_details) {
      setConvId(appStateContext?.state.currentChat?.id || '')
      setData(appStateContext?.state.currentChat?.extended_data?.experiment?.setup_details)
    }
  }, [appStateContext?.state.currentChat?.extended_data])

  if (!data) {
    return <Typography>Loading ... or Error</Typography>
  }

  return (
    <Grid
      container
      columnSpacing={2}
      rowSpacing={4}>
      <DateFormField
        name="Experiment Start Date"
        stepName="setup_details"
        fieldName="experiment_start_date"
        convId={convId}
        gridSize={6}
        data={data?.experiment_start_date}
        disabled={data?.experiment_start_date?.disabled}
        onError={onError}
      />
      <DateFormField
        name="Experiment End Date"
        stepName="setup_details"
        fieldName="experiment_end_date"
        convId={convId}
        gridSize={6}
        data={data?.experiment_end_date}
        disabled={data?.experiment_end_date?.disabled}
        onError={onError}
      />

      <DateFormField
        name="Final Result Submission Date"
        stepName="setup_details"
        fieldName="final_result_submission_date"
        convId={convId}
        gridSize={6}
        data={data?.final_result_submission_date}
        disabled={data?.final_result_submission_date?.disabled}
        tooltipInfo="This should be an estimated submission date only."
        onError={onError}
      />
      <NumberFormField
        name="Experiment Budget"
        stepName="setup_details"
        fieldName="experiment_budget"
        convId={convId}
        gridSize={6}
        data={data?.experiment_budget}
        disabled={data?.experiment_budget?.disabled}
        onError={onError}
      />
      <MultiSelectFormField
        name="Analytical Team"
        stepName="setup_details"
        fieldName="analytical_team"
        convId={convId}
        gridSize={12}
        data={data?.analytical_team}
        disabled={data?.analytical_team?.disabled}
        onError={onError}
      />

      <Grid size={12}>
        <Divider className={styles.divider} />
        <Typography
          className={styles.sectionTitle}
          variant="h6">
          Campaign & Platform Details
        </Typography>
      </Grid>

      <TextFormField
        name="Campaign Name"
        stepName="setup_details"
        fieldName="campaign_name"
        convId={convId}
        gridSize={6}
        rows={1}
        data={data?.campaign_name}
        disabled={data?.campaign_name?.disabled}
        onError={onError}
      />
      <TextFormField
        name="Campaign Objective"
        stepName="setup_details"
        fieldName="campaign_objective"
        convId={convId}
        gridSize={6}
        rows={1}
        data={data?.campaign_objective}
        disabled={data?.campaign_objective?.disabled}
        onError={onError}
      />
      <MultiSelectFormField
        name="Platform/Partner"
        stepName="setup_details"
        fieldName="platform_partner"
        convId={convId}
        gridSize={6}
        data={data?.platform_partner}
        disabled={data?.platform_partner?.disabled}
        onError={onError}
      />
      <TextFormField
        name="Format"
        stepName="setup_details"
        fieldName="format"
        convId={convId}
        gridSize={6}
        rows={1}
        data={data?.format}
        disabled={data?.format?.disabled}
        tooltipInfo="Indicate the ad format(s) used in the test (e.g., Facebook Stories, Reels)."
        onError={onError}
      />
      <MultiSelectFormField
        name="Buying Type"
        stepName="setup_details"
        fieldName="buying_type"
        convId={convId}
        gridSize={6}
        data={data?.buying_type}
        disabled={data?.buying_type?.disabled}
        onError={onError}
      />
      <MultiSelectFormField
        name="Exclusions"
        stepName="setup_details"
        fieldName="exclusions"
        convId={convId}
        gridSize={6}
        data={data?.exclusions}
        disabled={data?.exclusions?.disabled}
        onError={onError}
      />
      <Grid size={12}>
        <Divider className={styles.divider} />
        <Typography
          className={styles.sectionTitle}
          variant="h6">
          File Upload
        </Typography>
      </Grid>

      <Grid size={12}>
        <ul className={styles.infoList}>
          <li>Ensure that all mandatory files are included before submission to avoid delays</li>
          <li>Please refer to the templates in order to properly set and upload your files</li>
          <li>If you have any questions about which files to upload, please contact support</li>
        </ul>
      </Grid>

      <Grid
        size={12}
        gap={5}
        display={'flex'}
        flexDirection={'column'}>
        <FileInputFormField
          data={data.creatives}
          name="1. Creatives"
          stepName="setup_details"
          convId={convId}
          fieldName="creatives"
          subtitle="Mandatory for experiments where the creative is the test variable"
          onError={onError}
        />
        <FileInputFormField
          data={data.overall_test_structure_and_design}
          name="2. Overall Test Structure and Design"
          stepName="setup_details"
          convId={convId}
          fieldName="overall_test_structure_and_design"
          subtitle="Mandatory for all experiment types"
          templateUrl={appStateContext?.state.frontendSettings?.template?.overall_test_structure_and_design}
          onError={onError}
        />
        <FileInputFormField
          data={data.media_plan}
          name="3. Media Plan"
          stepName="setup_details"
          convId={convId}
          fieldName="media_plan"
          subtitle="Mandatory for Event Impact experiments"
          onError={onError}
        />
        <FileInputFormField
          data={data.budget_sufficiency_tool}
          name="4. Budget Sufficiency Tool"
          stepName="setup_details"
          convId={convId}
          fieldName="budget_sufficiency_tool"
          subtitle="Recommended for Event Impact experiments to assess budget sufficiency"
          templateUrl={appStateContext?.state.frontendSettings?.template?.budget_sufficiency_tool}
          onError={onError}
        />
        <FileInputFormField
          data={data.mid_campaign_report}
          name="5. Mid-Campaign Report"
          stepName="setup_details"
          convId={convId}
          fieldName="mid_campaign_report"
          subtitle="Mandatory for Event Impact experiments"
          onError={onError}
        />
      </Grid>
    </Grid>
  )
}
